import React, { useEffect } from 'react';
import { OptionSelectComponent, PersonDemographicComponent, RadioGroupComponent, TextQuestionComponent, ValidationResult } from '@myriadgenetics/mgh-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { CleanedRadioGroupOption } from '../radio/constants';
import TextQuestion from '../text-question';
import OptionSelect from '../option-select';
import { H3, Text2 } from '../../../elements/typography';
import { formFields } from './form-fields';
import { getSlugFromPath, isDemoUrl } from '../../../helpers/url';
import { getAge, getDateFromInput, isValidDate } from '../helper';

import './index.scss';
import wand from './wand.png';
import RadioGroup from '../radio';

interface Props {
  compDef: any | PersonDemographicComponent;
  responses: Record<string, any>;
  onResponseUpdated: (key: string, val: any) => void;
  validationResult?: ValidationResult;
}

function PersonDemographic({ compDef, responses, onResponseUpdated, validationResult }: Props) {
  const { t } = useTranslation();
  const pd = compDef as PersonDemographicComponent;
  const mghOrderData = responses.mghOrderData;

  // CRM needs a phoneType, it doesn't matter which one, and if that ever changes we can remove this.
  useEffect(() => {
    const curAnswer = get(responses, pd.data.answerKey) || {};
    if (!curAnswer.phoneType) {
      onResponseUpdated(`${pd.data.answerKey}.phoneType`, 'mobile');
    }
  }, [responses, onResponseUpdated, pd.data.answerKey]);

  useEffect(() => {
    const curAnswer = get(responses, pd.data.answerKey) || {};

    const hasExistingData = curAnswer.firstName || curAnswer.lastName || curAnswer.dob || curAnswer.email;
    if (mghOrderData && !hasExistingData) {
      const femaleOption = { optionKey: '1', optionValue: 'personDemographic.female' };
      const maleOption = { optionKey: '2', optionValue: 'personDemographic.male' };
      const mghOrderSab = mghOrderData?.patient?.sex === 'F' ? [femaleOption] : mghOrderData?.patient?.sex === 'M' ? [maleOption] : null;

      const prepopulatedData = {
        ...curAnswer,
        firstName: mghOrderData.patient.firstName || '',
        phoneType: 'mobile',
        lastName: mghOrderData.patient.lastName || '',
        dob: mghOrderData.patient.dob || '',
        age: isValidDate(mghOrderData.patient.dob) ? getAge(getDateFromInput(mghOrderData.patient.dob)) : null,
        sex: mghOrderData?.patient.sex ? mghOrderSab : null,
        phone: mghOrderData.patient.phone || '',
        email: mghOrderData.patient.email || '',
        address: mghOrderData.patient.address || '',
        city: mghOrderData.patient.city || '',
        state: mghOrderData.patient.state
          ? [{ optionKey: 999, optionValue: `personDemographic.states.${mghOrderData.patient.state.toLowerCase()}` }]
          : undefined,
        postalCode: mghOrderData.patient.postalCode || '',
      };

      onResponseUpdated(pd.data.answerKey, prepopulatedData);
    }
  }, [mghOrderData, onResponseUpdated, pd.data.answerKey, responses]);

  const updateResponse = (answerKey: string, val: boolean | string | CleanedRadioGroupOption[]) => {
    onResponseUpdated(`${pd.data.answerKey}.${answerKey}`, val);
    if (answerKey === 'dob') {
      onResponseUpdated(`${pd.data.answerKey}.age`, isValidDate(val as string) ? getAge(getDateFromInput(val as string)) : null);
    }
  };
  // cast as any so we don't have to create index signatures
  // anytime we see "as any" it's usually in reference to usage of string index
  // and TS cannot guarantee that string
  const validate = (subCompDef: TextQuestionComponent | OptionSelectComponent | RadioGroupComponent) =>
    validationResult && validationResult.errorMessage && (validationResult.errorMessage as any)[subCompDef.data.answerKey];

  const FormFields = formFields(pd);

  const isFieldDisplayed = (field: string): boolean =>
    Object.keys(FormFields).some((fieldComp) => fieldComp.startsWith(field) && (pd.data.fields as any)[field] && (pd.data.fields as any)[field] !== 'HIDDEN');
  const curAnswer = get(responses, pd.data.answerKey);
  const autoFill = () => {
    const autofillDob = '2000-10-10';
    onResponseUpdated(pd.data.answerKey, {
      firstName: 'Test',
      phoneType: 'mobile',
      lastName: 'Testerson',
      dob: autofillDob,
      age: getAge(getDateFromInput(autofillDob)),
      phone: '555-523-4234',
      email: 'test@test.com',
      address: 'Some Small St.',
      city: 'Somewhere',
      state: [{ optionKey: 6, optionValue: 'personDemographic.states.colorado' }],
      postalCode: '23432',
      sex: [{ optionKey: '1', optionValue: 'personDemographic.female' }],
      ...curAnswer,
    });
  };

  const slug = getSlugFromPath(window.location) as string;
  const isDemo = isDemoUrl(slug);
  return (
    <div className="person-demographic">
      <H3 className="person-demographic__title">{t(pd.data.questionKey)}</H3>
      {pd.data.descriptionKey && <Text2 className="person-demographic__description">{t(pd.data.descriptionKey)}</Text2>}
      <div className="person-demographic__fields">
        {isFieldDisplayed('firstName') && (
          <TextQuestion
            key={'firstName'}
            className="person-demographic-field__first-name"
            compDef={FormFields.firstNameQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.firstNameQuestion)}
          />
        )}
        {isFieldDisplayed('lastName') && (
          <TextQuestion
            key={'lastName'}
            className="person-demographic-field__last-name"
            compDef={FormFields.lastNameQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.lastNameQuestion)}
          />
        )}
        {isFieldDisplayed('dob') && (
          <TextQuestion
            key={'dob'}
            className="person-demographic-field__dob"
            compDef={FormFields.dobQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.dobQuestion)}
          />
        )}
        {isFieldDisplayed('sex') && (
          <RadioGroup
            key={'sex'}
            className="person-demographic-field__sex"
            compDef={FormFields.sexQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.sexQuestion)}
          />
        )}
        {isFieldDisplayed('phone') && (
          <TextQuestion
            key={'phone'}
            className="person-demographic-field__phone"
            compDef={FormFields.phoneQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.phoneQuestion)}
          />
        )}
        {isFieldDisplayed('email') && (
          <TextQuestion
            key={'email'}
            className="person-demographic-field__email"
            compDef={FormFields.emailQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.emailQuestion)}
            asTextArea={true}
          />
        )}
        {isFieldDisplayed('address') && (
          <TextQuestion
            key={'address'}
            className="person-demographic-field__address"
            compDef={FormFields.addressQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.addressQuestion)}
            asTextArea={true}
          />
        )}
        {isFieldDisplayed('city') && (
          <TextQuestion
            key={'city'}
            className="person-demographic-field__city"
            compDef={FormFields.cityQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.cityQuestion)}
          />
        )}
        {isFieldDisplayed('state') && (
          <OptionSelect
            key={'state'}
            className="person-demographic-field__state"
            compDef={FormFields.stateQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.stateQuestion)}
          />
        )}
        {isFieldDisplayed('postalCode') && (
          <TextQuestion
            key={'postalCode'}
            className="person-demographic-field__postal-code"
            compDef={FormFields.postalCodeQuestion}
            responses={curAnswer}
            onResponseUpdated={updateResponse}
            validationResult={validate(FormFields.postalCodeQuestion)}
          />
        )}
      </div>
      {isDemo && <img alt="magic" className="person-demographic__wand" src={wand} onClick={autoFill} />}
    </div>
  );
}

export default PersonDemographic;
